import React, { useEffect, useState } from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Trophy, Calendar, Shield, Flag } from "lucide-react";
import { api } from "../lib/api";

const LabeledBadge = ({ text, tone = "neutral" }) => {
  const styles = {
    W: "bg-[#6CABDD] text-[var(--brand-ink)]",
    D: "bg-[#E2E8F0] text-[var(--brand-ink)]",
    L: "bg-[#0A0F17] text-white",
    neutral: "bg-[#F1F5F9] text-[var(--brand-body)]",
  };
  return (
    <span
      className={`inline-flex h-6 w-6 items-center justify-center rounded-sm font-display text-xs font-black ${styles[tone] || styles.neutral}`}
    >
      {text}
    </span>
  );
};

export default function ApiFootballStatsPage() {
  const [season, setSeason] = useState(2026);
  const [standings, setStandings] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadData = async (selectedSeason) => {
    setLoading(true);
    setError("");
    try {
      const [sRes, fRes] = await Promise.all([
        api.get(`/apifootball/standings?season=${selectedSeason}`),
        api.get(`/apifootball/fixtures?season=${selectedSeason}`),
      ]);
      setStandings(sRes.data?.standings || []);
      setFixtures(fRes.data?.fixtures || []);
    } catch (err) {
      console.error(err);
      setError("Unable to load statistics. Please make sure the Football API key is configured correctly.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(season);
  }, [season]);

  const coventryTeamId = 1076;

  const getResultTone = (f) => {
    const goals = f.goals || {};
    const teams = f.teams || {};
    if (goals.home == null || goals.away == null) return "neutral";
    const isCoventryHome = teams.home?.id === coventryTeamId;
    const covScore = isCoventryHome ? goals.home : goals.away;
    const oppScore = isCoventryHome ? goals.away : goals.home;
    if (covScore > oppScore) return "W";
    if (covScore < oppScore) return "L";
    return "D";
  };

  return (
    <div className="min-h-screen bg-[var(--page-bg)] text-[var(--brand-ink)]" data-testid="page-football-stats">
      <Header />
      
      <main className="mx-auto max-w-7xl px-5 pt-4 pb-16 sm:px-8 sm:pt-10">
        <div className="mb-6 border-b border-black/10 pb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-brand-accent">
              <Trophy size={16} strokeWidth={2.5} />
              <span className="label-kicker">Coventry Stats (API-Football)</span>
            </div>
            <h2 className="brand-headline mt-2 font-display text-3xl font-black tracking-tight text-[var(--brand-ink)] sm:text-4xl">
              League Standings & Results
            </h2>
            <p className="mt-2 max-w-xl text-sm text-[var(--brand-body)]">
              Championship table and completed fixtures fetched securely from api-football.com.
            </p>
          </div>
          <div className="flex items-center gap-3 self-start sm:self-auto">
            <span className="text-xs font-semibold text-[var(--brand-body)] uppercase tracking-wider">Season:</span>
            <select
              value={season}
              onChange={(e) => setSeason(Number(e.target.value))}
              className="rounded-md border border-black/15 bg-white px-3 py-1.5 text-sm font-semibold outline-none focus:border-[#0A0F17]"
              data-testid="stats-season-select"
            >
              <option value={2026}>2026/27</option>
              <option value={2025}>2025/26</option>
              <option value={2024}>2024/25</option>
            </select>
          </div>
        </div>

        {error ? (
          <p className="rounded-md border-l-2 border-[#E63946] bg-[#E63946]/5 px-4 py-3 text-sm font-semibold text-[var(--brand-ink)]">
            {error}
          </p>
        ) : null}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Standings Column */}
          <div className="rounded-md border border-black/10 bg-white lg:col-span-2" data-testid="apifootball-table">
            <div className="flex items-center justify-between border-b border-black/10 px-6 py-4">
              <h3 className="font-display text-lg font-bold text-[var(--brand-ink)] flex items-center gap-2">
                <Shield size={18} className="text-[#6CABDD]" />
                Championship Table
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-black/5 bg-[#F8FAFC] text-[var(--brand-body)]">
                    <th className="px-4 py-3 label-kicker">#</th>
                    <th className="px-3 py-3 label-kicker">Team</th>
                    <th className="px-2 py-3 text-right label-kicker">P</th>
                    <th className="hidden px-2 py-3 text-right label-kicker sm:table-cell">W</th>
                    <th className="hidden px-2 py-3 text-right label-kicker sm:table-cell">D</th>
                    <th className="hidden px-2 py-3 text-right label-kicker sm:table-cell">L</th>
                    <th className="px-2 py-3 text-right label-kicker">GD</th>
                    <th className="px-4 py-3 text-right label-kicker">Pts</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    ["s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8"].map((id) => (
                      <tr key={id} className="animate-pulse border-b border-black/5">
                        <td className="px-4 py-4"><div className="h-4 w-4 rounded-sm bg-black/5" /></td>
                        <td className="px-3 py-4"><div className="h-4 w-32 rounded-sm bg-black/5" /></td>
                        <td className="px-2 py-4"><div className="ml-auto h-4 w-6 rounded-sm bg-black/5" /></td>
                        <td className="hidden px-2 py-4 sm:table-cell"><div className="ml-auto h-4 w-6 rounded-sm bg-black/5" /></td>
                        <td className="hidden px-2 py-4 sm:table-cell"><div className="ml-auto h-4 w-6 rounded-sm bg-black/5" /></td>
                        <td className="hidden px-2 py-4 sm:table-cell"><div className="ml-auto h-4 w-6 rounded-sm bg-black/5" /></td>
                        <td className="px-2 py-4"><div className="ml-auto h-4 w-6 rounded-sm bg-black/5" /></td>
                        <td className="px-4 py-4"><div className="ml-auto h-4 w-8 rounded-sm bg-black/5" /></td>
                      </tr>
                    ))
                  ) : standings.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-sm text-[var(--brand-body)] italic">
                        No standings data available for this season.
                      </td>
                    </tr>
                  ) : (
                    standings.map((row) => {
                      const isCoventry = row.team?.id === coventryTeamId;
                      return (
                        <tr
                          key={row.team?.id || row.team?.name}
                          className={`border-b border-black/5 transition-colors hover:bg-black/2 ${
                            isCoventry ? "bg-[#6CABDD]/10 font-bold" : ""
                          }`}
                        >
                          <td className="px-4 py-3 font-display font-black text-[var(--brand-body)]">
                            {row.rank}
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-2">
                              {row.team?.logo ? (
                                <img
                                  src={row.team.logo}
                                  alt=""
                                  loading="lazy"
                                  className="h-5 w-5 object-contain"
                                />
                              ) : null}
                              <span className="truncate max-w-[150px] sm:max-w-none">
                                {row.team?.name}
                              </span>
                            </div>
                          </td>
                          <td className="px-2 py-3 text-right font-semibold text-[var(--brand-ink)]">
                            {row.all?.played}
                          </td>
                          <td className="hidden px-2 py-3 text-right text-[var(--brand-body)] sm:table-cell">
                            {row.all?.win}
                          </td>
                          <td className="hidden px-2 py-3 text-right text-[var(--brand-body)] sm:table-cell">
                            {row.all?.draw}
                          </td>
                          <td className="hidden px-2 py-3 text-right text-[var(--brand-body)] sm:table-cell">
                            {row.all?.lose}
                          </td>
                          <td
                            className={`px-2 py-3 text-right text-[var(--brand-body)] ${
                              (row.goalsDiff || 0) > 0
                                ? "text-emerald-600"
                                : (row.goalsDiff || 0) < 0
                                ? "text-red-500"
                                : ""
                            }`}
                          >
                            {row.goalsDiff > 0 ? `+${row.goalsDiff}` : row.goalsDiff}
                          </td>
                          <td className="px-4 py-3 text-right font-display font-black text-[var(--brand-ink)]">
                            {row.points}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Completed Fixtures Column */}
          <div className="rounded-md border border-black/10 bg-white" data-testid="apifootball-fixtures">
            <div className="flex items-center border-b border-black/10 px-6 py-4">
              <h3 className="font-display text-lg font-bold text-[var(--brand-ink)] flex items-center gap-2">
                <Calendar size={18} className="text-[#6CABDD]" />
                Completed Results
              </h3>
            </div>
            <div className="divide-y divide-black/5 overflow-y-auto max-h-[550px] scrollbar-thin">
              {loading ? (
                ["f1", "f2", "f3", "f4", "f5"].map((id) => (
                  <div key={id} className="animate-pulse p-4 flex items-center justify-between gap-3">
                    <div className="space-y-2 flex-1">
                      <div className="h-4 w-2/3 rounded-sm bg-black/5" />
                      <div className="h-3 w-1/3 rounded-sm bg-black/5" />
                    </div>
                    <div className="h-6 w-12 rounded-sm bg-black/5" />
                  </div>
                ))
              ) : fixtures.length === 0 ? (
                <p className="p-8 text-center text-sm text-[var(--brand-body)] italic">
                  No completed fixtures found for this season.
                </p>
              ) : (
                fixtures.map((f) => {
                  const goals = f.goals || {};
                  const teams = f.teams || {};
                  const fixtureDate = f.fixture?.date ? new Date(f.fixture.date) : null;
                  return (
                    <div key={f.fixture?.id} className="p-4 flex items-center justify-between gap-3 hover:bg-black/2 transition-colors">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-semibold text-[var(--brand-body)]">
                          {teams.home?.name} vs {teams.away?.name}
                        </p>
                        <p className="mt-1 text-[10px] text-[var(--brand-body)]/60">
                          {fixtureDate ? fixtureDate.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="font-display text-sm font-black text-[var(--brand-ink)]">
                          {goals.home}–{goals.away}
                        </span>
                        <LabeledBadge text={getResultTone(f)} tone={getResultTone(f)} />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
